import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import { HydraCollectionResponse } from '@vs-shop/schemas/openapi/response';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import type { Schema as SchemaType } from 'effect';
import { Effect } from 'effect';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

import { HTTP_METHOD, PRODUCT_VARIANTS_ENDPOINT } from './_shared';

export type MakeListProductVariantByCodesDeps<ProductVariantsByCodes extends SchemaType.Top> = {
  readonly schemas: {
    readonly productVariantsByCodes: ProductVariantsByCodes;
  };
};

export const makeListProductVariantByCodes = <ProductVariantsByCodes extends SchemaType.Top>(
  deps: MakeListProductVariantByCodesDeps<ProductVariantsByCodes>
) => {
  const ListProductVariantByCodesError = createBackendApiError({
    domain: 'ProductVariant',
    endpoint: 'ListByCodes',
    path: PRODUCT_VARIANTS_ENDPOINT,
    identifierField: 'codes',
  });

  const getProductVariantByCodesFetch = (codes: string[]) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(PRODUCT_VARIANTS_ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json')
        .fetch({ query: { 'code[]': codes } });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${PRODUCT_VARIANTS_ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'productVariants.count': codes.length,
        },
      })
    );

  const listProductVariantByCodes = (codes: string[]) =>
    getProductVariantByCodesFetch(codes).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) =>
            schemaBodyJsonClean(HydraCollectionResponse(deps.schemas.productVariantsByCodes))(
              response
            ),
          404: (response) =>
            Effect.fail(new ListProductVariantByCodesError.NotFoundError({ codes, response })),
          orElse: (response) =>
            Effect.fail(new ListProductVariantByCodesError.ApiRequestError({ codes, response })),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.ProductVariantListByCodes,
          'Debug',
          `Successfully resolved ${String(result['hydra:member'].length)} product variants for ${String(codes.length)} codes`
        )
      ),
      Effect.withSpan('api.productVariant.listByCodes', {
        kind: 'internal',
        attributes: {
          'productVariants.count': codes.length,
        },
      })
    );

  return { listProductVariantByCodes, ListProductVariantByCodesError };
};
